import { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon, AddIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useClickOutside } from '@/hooks/useClickOutside';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { Button } from '@/components/ui/Button/Button';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { ProjectNotesPopoverNoteItem } from './ProjectNotesPopoverNoteItem';
import { PROJECTS_TABLE_ICON_ACTION_WRAP_SELECTOR } from './projectNotesPopoverConstants';
import { useProjectNotesEditor } from './useProjectNotesEditor';
import type { ProjectNotesPopoverServerProps } from './ProjectNotesPopover.types';
import '@/components/ui/PopoverInlineLoading.css';

/**
 * Попап заметок существующего проекта: синхронизация с сервером и кэшем React Query.
 */
export function ProjectNotesPopoverServer({
  open,
  onClose,
  project,
  anchorEl,
}: ProjectNotesPopoverServerProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const projectId = project.id;

  const { shouldRender, contentClasses, requestClose, isExiting } = useOverlayAnimation(open, onClose, true);

  const {
    notes: notesSorted,
    isPending: notesPending,
    isError: notesError,
    error: notesFetchError,
    getNoteContent,
    changeNote: handleNoteChange,
    addNote: handleAddNote,
    deleteNote: handleDelete,
    deletingId,
    isDeleteBusy,
    isAddBusy,
  } = useProjectNotesEditor(projectId, open);

  usePopoverPositioning({
    anchorEl,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionX: 'left',
    preferredPositionY: 'bottom',
    verticalAlign: 'center',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1000,
    priorityAxis: 'x',
    clampOnScrollFollow: true,
    onAnchorDetached: requestClose,
  });

  const anchorExcludeRef = useRef<HTMLElement | null>(null);
  anchorExcludeRef.current = anchorEl;

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    excludeRefs: [anchorExcludeRef as React.RefObject<HTMLElement | null>],
    excludeSelector: PROJECTS_TABLE_ICON_ACTION_WRAP_SELECTOR,
    enabled: shouldRender,
    capture: false,
  });

  useEffect(() => {
    if (!shouldRender) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [shouldRender, requestClose]);

  const popoverContent = (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: -9999,
        top: -9999,
        pointerEvents: isExiting ? 'none' : 'auto',
      }}
    >
      <OverlayIntrinsicSize>
        <div
          className={`project-notes-popover ${contentClasses}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="project-notes-popover-title"
        >
      <div className="project-notes-popover__header">
        <h2 id="project-notes-popover-title" className="project-notes-popover__title">
          Заметки: {project.name || 'Проект'}
        </h2>
        <IconButton aria-label="Закрыть" onClick={requestClose} size="m" iconSize={20} variant="danger" className="project-notes-popover__close">
          <CloseIcon />
        </IconButton>
      </div>
      <div className="project-notes-popover__body">
        <OverlayScrollArea
          className="project-notes-popover__scroll"
          osOptions={{ overflow: { x: 'visible', y: 'scroll' } }}
        >
          <div className="project-notes-popover__scroll-pad">
            {notesPending ? (
              <div className="project-popover-inline-loading" aria-busy="true" aria-live="polite">
                <span className="project-popover-inline-loading__spinner" aria-label="Загрузка заметок" />
              </div>
            ) : notesError ? (
              <div className="project-notes-popover__empty" role="alert">
                <p>{notesFetchError?.message || 'Не удалось загрузить заметки'}</p>
              </div>
            ) : (
              <ul className="project-notes-popover__list">
                {notesSorted.length === 0 ? (
                  <li className="project-notes-popover__empty">
                    <i className="fa-solid fa-pen-clip project-notes-popover__empty-icon" aria-hidden="true" />
                    <p>Нет заметок</p>
                  </li>
                ) : (
                  notesSorted.map((note) => (
                    <ProjectNotesPopoverNoteItem
                      key={note.note_id}
                      note={note}
                      content={getNoteContent(note)}
                      onContentChange={handleNoteChange}
                      onDelete={handleDelete}
                      isDeleting={deletingId === note.note_id}
                      deleteDisabled={isDeleteBusy}
                    />
                  ))
                )}
              </ul>
            )}
          </div>
        </OverlayScrollArea>
        <div className="project-notes-popover__add-btn-wrap">
          <Button
            type="button"
            variant="outlined"
            onClick={handleAddNote}
            disabled={isAddBusy}
            className="project-notes-popover__add-btn"
          >
            <AddIcon size={18} />
            Добавить заметку
          </Button>
        </div>
      </div>
        </div>
      </OverlayIntrinsicSize>
    </div>
  );

  return shouldRender ? createPortal(popoverContent, document.body) : null;
}
